"use client";

import type React from "react";

import { useState } from "react";
import {
  Card,
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  useTheme,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import type { WorkOrder, Segment } from "./lib/types";
import type { SettingsLocation } from "models/lookupTypes";
import { LocationOn as LocationIcon } from "@mui/icons-material";
import { SegmentItem } from "./segment-item";
import type { DragData } from "./lib/hooks/use-drag-drop";
import {
  getBorderLeftColor,
  getWorkOrderStatusColor,
  getWorkOrderStatusName,
} from "./lib/utils/schedular-utils";
import {
  getSegmentDragHours,
  isSegmentDraggable,
} from "./lib/utils/segment-scheduling-utils";
import {
  workOrderHeaderHoverStyles,
  priorityChipDotStyles,
  segmentConnectorVerticalStyles,
  segmentConnectorHorizontalStyles,
} from "./schedular-styles";

interface WorkOrderItemProps {
  workOrder: WorkOrder;
  reserved: number;
  isBroken: boolean;
  locations?: SettingsLocation[];
  isSegmentBroken?: (segmentId: string) => boolean;
  onDragStart: (e: React.DragEvent, data: DragData) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onUpdateSegmentHours?: (segmentId: string, newHours: number) => void;
  onBreakSegment?: (segmentId: string) => void;
  onConcurrencyError?: (message: string) => void;
}

export function WorkOrderItem({
  workOrder,
  reserved,
  locations = [],
  isSegmentBroken,
  onDragStart,
  onDragEnd,
  onUpdateSegmentHours,
  onBreakSegment,
  onConcurrencyError,
}: WorkOrderItemProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [expanded, setExpanded] = useState(false); // Start collapsed by default
  const [isDragging, setIsDragging] = useState(false);

  const workOrderLocation = workOrder.locationId
    ? locations.find((loc) => loc.identity === workOrder.locationId)
    : undefined;
  const locationName = workOrderLocation?.displayText ?? "";
  const locationAbbr =
    locationName.length > 6
      ? `${locationName.substring(0, 6)}...`
      : locationName;

  const draggableSegments = workOrder.segmentList.filter((segment) =>
    isSegmentDraggable(segment),
  );

  const hasAnyBrokenSegment = workOrder.segmentList.some((segment) =>
    isSegmentBroken ? isSegmentBroken(segment.id) : false,
  );
  const canDragWorkOrder =
    !hasAnyBrokenSegment && draggableSegments.length > 0;

  // Handle drag start for the entire work order
  const handleWorkOrderDragStart = (e: React.DragEvent) => {
    if (!canDragWorkOrder) {
      e.preventDefault();
      return;
    }
    setIsDragging(true);

    // Calculate total hours from all segments
    const totalHours = draggableSegments.reduce(
      (sum, seg) => sum + getSegmentDragHours(seg),
      0,
    );

    const dragData: DragData = {
      type: "work-order",
      id: workOrder.id,
      hours: totalHours,
      workOrderId: workOrder.id,
      workOrderNumber: workOrder.invoiceNumber,
      customerName: workOrder.customerName,
      customerNumber: workOrder.customerNumber,
      myReference: workOrder.myReference,
      woStatusId: workOrder.woStatusId,
      locationId: workOrder.locationId,
      segments: draggableSegments.map((seg) => ({
        id: seg.id,
        name: seg.name,
        estimatedHours: getSegmentDragHours(seg),
      })),
    };

    onDragStart(e, dragData);
  };

  const handleWorkOrderDragEnd = (e: React.DragEvent) => {
    setIsDragging(false);
    onDragEnd(e);
  };

  // Using utility function from schedular-utils
  const formatHours = (hours: number) => {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}.${m.toString().padStart(2, "0")}`;
  };

  const totalEstimatedHours = workOrder.segmentList.reduce(
    (sum, seg) => sum + seg.estimatedHours,
    0,
  );

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const getSegmentReserved = (segment: Segment): number => {
    // Simplified: distribute reserved hours evenly across segments
    // In a real app, you'd track reserved hours per segment
    return workOrder.segmentList.length > 0
      ? reserved / workOrder.segmentList.length
      : 0;
  };

  const checkIfSegmentBroken = (segment: Segment): boolean => {
    return isSegmentBroken ? isSegmentBroken(segment.id) : false;
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Work Order Header - Draggable */}
      <Tooltip
        title={
          !canDragWorkOrder
            ? hasAnyBrokenSegment
              ? "Cannot drag: Some segments are broken down"
              : "Cannot drag: No segments available"
            : "Drag to schedule all segments"
        }
        placement="top"
        arrow
      >
        <Card
          draggable={canDragWorkOrder}
          onDragStart={handleWorkOrderDragStart}
          onDragEnd={handleWorkOrderDragEnd}
          elevation={isDragging ? 8 : 1}
          sx={{
            p: 0.75,
            bgcolor: isDragging ? "primary.light" : "background.paper",
            border: isDragging ? "2px solid" : "1px solid",
            borderColor: isDragging ? "primary.main" : "divider",
            borderRadius: 1.5,
            mb: expanded ? 0.5 : 0,
            borderLeft: `3px solid`,
            // Use WorkOrderStatus color (from cache) if available, fallback to priority color
            borderLeftColor: getBorderLeftColor(
              workOrder.woStatusId,
              workOrder.priority,
            ),
            boxShadow: isDragging
              ? "0 12px 32px rgba(0, 0, 0, 0.15)"
              : "0 1px 4px rgba(0, 0, 0, 0.08)",
            cursor: canDragWorkOrder ? "grab" : "not-allowed",
            "&:active": {
              cursor: canDragWorkOrder ? "grabbing" : "not-allowed",
            },
            opacity: isDragging ? 0.5 : 1,
            transform: isDragging ? "rotate(2deg) scale(0.95)" : "scale(1)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": canDragWorkOrder
              ? {
                  borderColor: "primary.light",
                  transform: "translateY(-2px)",
                }
              : {},
          }}
        >
          <Box
            sx={{
              ...workOrderHeaderHoverStyles,
              gap: 0.25,
              p: 0.15,
            }}
            onClick={handleToggleExpand}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(e);
                }}
                sx={{
                  color: "text.secondary",
                  p: 0.25,
                  minWidth: 28,
                  width: 28,
                  height: 28,
                  "&:hover": {
                    bgcolor: "transparent",
                  },
                }}
              >
                {expanded ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
              </IconButton>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                    flexWrap: "wrap",
                    lineHeight: 1.2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "text.primary",
                    }}
                  >
                    {workOrder.invoiceNumber || workOrder.workOrderNumber}
                  </Typography>

                  {locationName && (
                    <Tooltip title={locationName} arrow placement="top">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.25,
                          bgcolor: "#1976d2",
                          color: "white",
                          py: 0.25,
                          px: 0.5,
                          borderRadius: 0.5,
                          fontSize: ".55rem",
                          maxWidth: 80,
                          flexShrink: 0,
                        }}
                      >
                        <LocationIcon sx={{ fontSize: "0.7rem" }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: ".55rem",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {locationAbbr}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                  <Chip
                    size="small"
                    label=""
                    sx={{
                      ...priorityChipDotStyles,
                      // Use WorkOrderStatus color if available, fallback to priority color
                      bgcolor: getWorkOrderStatusColor(workOrder.woStatusId),
                    }}
                  />
                </Box>
                {/* Customer Name */}
                {workOrder.customerName && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.primary",
                      fontSize: "0.7rem",
                      lineHeight: 1.2,
                      display: "block",
                      fontWeight: 500,
                    }}
                  >
                    {workOrder.customerName}
                  </Typography>
                )}
                {/* Description */}
                {workOrder.description && (
                  <Tooltip title={workOrder.description} arrow placement="top">
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.65rem",
                        lineHeight: 1.2,
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {workOrder.description.length > 30
                        ? `${workOrder.description.substring(0, 30)}...`
                        : workOrder.description}
                    </Typography>
                  </Tooltip>
                )}
                {/* Unit Model */}
                {workOrder.unitModel && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.65rem",
                      lineHeight: 1.2,
                      display: "block",
                    }}
                  >
                    <strong>Unit:</strong> {workOrder.unitModel}
                  </Typography>
                )}
                {/* Status - fetched from cached WorkOrderStatuses using woStatusId */}
                {getWorkOrderStatusName(workOrder.woStatusId) && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.65rem",
                      lineHeight: 1.2,
                      display: "block",
                      fontStyle: "italic",
                    }}
                  >
                    <strong>WO Status:</strong>{" "}
                    {getWorkOrderStatusName(workOrder.woStatusId)}
                  </Typography>
                )}

                {/* Status - fetched from cached WorkOrderStatuses using woStatusId */}

                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    lineHeight: 1.2,
                    display: "block",
                    fontStyle: "italic",
                  }}
                >
                  <strong>Doc Status:</strong> {workOrder.status}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    lineHeight: 1.2,
                  }}
                >
                  {formatHours(totalEstimatedHours)}h •{" "}
                  {workOrder.segmentList.length}{" "}
                  {workOrder.segmentList.length === 1 ? "segment" : "segments"}
                </Typography>
                {/* Additional Work Order Details - Collapsible info */}
              </Box>
            </Box>
            {/* <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Chip
                size="small"
                label=""
                sx={{
                  ...priorityChipDotStyles,
                  // Use WorkOrderStatus color if available, fallback to priority color
                  bgcolor: getWorkOrderStatusColor(workOrder.woStatusId),
                }}
              />
            </Box> */}
          </Box>
        </Card>
      </Tooltip>

      {/* Segments - Indented to show hierarchy */}
      <Collapse in={expanded}>
        <Box sx={{ pl: 3, position: "relative" }}>
          {/* Visual connector line */}
          <Box sx={segmentConnectorVerticalStyles} />
          {workOrder.segmentList.map((segment, index) => (
            <Box key={segment.id} sx={{ position: "relative", mb: 1 }}>
              {/* Horizontal connector line */}
              <Box sx={segmentConnectorHorizontalStyles} />
              <SegmentItem
                segment={segment}
                workOrderName={workOrder.name}
                workOrderPriority={workOrder.priority}
                woStatusId={workOrder.woStatusId}
                reserved={getSegmentReserved(segment)}
                isBroken={checkIfSegmentBroken(segment)}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onUpdateHours={(segmentId, newHours) =>
                  onUpdateSegmentHours?.(segmentId, newHours)
                }
                onClick={() => onBreakSegment?.(segment.id)}
                onConcurrencyError={onConcurrencyError}
                // Work order fields for display and drag data
                workOrderNumber={workOrder.workOrderNumber}
                invoiceNumber={workOrder.invoiceNumber}
                customerName={workOrder.customerName}
                customerNumber={workOrder.customerNumber}
                unitNumber={workOrder.unitNumber}
                unitModel={workOrder.unitModel}
                vinNumber={workOrder.vinNumber}
                myReference={workOrder.myReference}
                woDescription={workOrder.description}
                woPriorityId={workOrder.woPriorityId}
              />
            </Box>
          ))}
          {workOrder.segmentList.length === 0 && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", py: 1, pl: 2 }}
            >
              No segments available
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
